import React, { useState, useEffect } from 'react';
import {
  Cloud, Server, Key, Shield, Network, HardDrive, Play, Square, Trash2,
  RefreshCw, Plus, CheckCircle, AlertTriangle, XCircle, Settings, Eye, EyeOff,
  ChevronDown, ChevronRight, Cpu, Globe, Lock, Database
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const AWSInfrastructure = ({ onUseInstances }) => {
  // State
  const [awsStatus, setAwsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Configuration
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configForm, setConfigForm] = useState({
    accessKeyId: '',
    secretAccessKey: '',
    region: 'us-east-1'
  });
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);

  // Provisioning
  const [showProvisionDialog, setShowProvisionDialog] = useState(false);
  const [provisionForm, setProvisionForm] = useState({
    count: 3,
    instanceType: 't3.medium',
    keyName: '',
    securityGroupId: '',
    subnetId: '',
    osType: 'ubuntu-22',
    volumeSize: 50,
    clusterName: 'kafka-cluster'
  });
  const [provisionLoading, setProvisionLoading] = useState(false);

  // AWS Resources
  const [vpcs, setVpcs] = useState([]);
  const [subnets, setSubnets] = useState([]);
  const [keyPairs, setKeyPairs] = useState([]);
  const [securityGroups, setSecurityGroups] = useState([]);
  const [instanceTypes, setInstanceTypes] = useState([]);
  const [instances, setInstances] = useState([]);
  const [selectedVpc, setSelectedVpc] = useState('');

  // Security Group Creation
  const [showCreateSGDialog, setShowCreateSGDialog] = useState(false);
  const [sgForm, setSgForm] = useState({ name: '', description: '' });

  const regions = [
    { value: 'us-east-1', label: 'US East (N. Virginia)' },
    { value: 'us-east-2', label: 'US East (Ohio)' },
    { value: 'us-west-1', label: 'US West (N. California)' },
    { value: 'us-west-2', label: 'US West (Oregon)' },
    { value: 'eu-west-1', label: 'EU (Ireland)' },
    { value: 'eu-west-2', label: 'EU (London)' },
    { value: 'eu-central-1', label: 'EU (Frankfurt)' },
    { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' }
  ];

  const osTypes = [
    { value: 'ubuntu-22', label: 'Ubuntu 22.04 LTS' },
    { value: 'ubuntu-24', label: 'Ubuntu 24.04 LTS' },
    { value: 'amazon-linux-2', label: 'Amazon Linux 2' }
  ];

  // Load AWS status on mount
  useEffect(() => {
    loadAwsStatus();
  }, []);

  const loadAwsStatus = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/aws/status`);
      const data = await res.json();
      setAwsStatus(data);

      if (data.configured) {
        await loadResources();
      }
    } catch (e) {
      setError('Failed to load AWS status: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadResources = async () => {
    try {
      const [vpcsRes, keyPairsRes, instanceTypesRes, instancesRes] = await Promise.all([
        fetch(`${API_URL}/aws/vpcs`).then(r => r.json()),
        fetch(`${API_URL}/aws/keypairs`).then(r => r.json()),
        fetch(`${API_URL}/aws/instance-types`).then(r => r.json()),
        fetch(`${API_URL}/aws/instances`).then(r => r.json())
      ]);

      setVpcs(vpcsRes);
      setKeyPairs(keyPairsRes);
      setInstanceTypes(instanceTypesRes);
      setInstances(instancesRes);

      if (vpcsRes.length > 0) {
        const defaultVpc = vpcsRes.find(v => v.isDefault) || vpcsRes[0];
        setSelectedVpc(defaultVpc.id);
        await loadVpcResources(defaultVpc.id);
      }
    } catch (e) {
      console.error('Failed to load AWS resources:', e);
    }
  };

  const loadVpcResources = async (vpcId) => {
    try {
      const [subnetsRes, sgRes] = await Promise.all([
        fetch(`${API_URL}/aws/subnets?vpcId=${vpcId}`).then(r => r.json()),
        fetch(`${API_URL}/aws/security-groups?vpcId=${vpcId}`).then(r => r.json())
      ]);
      setSubnets(subnetsRes);
      setSecurityGroups(sgRes);
    } catch (e) {
      console.error('Failed to load VPC resources:', e);
    }
  };

  const handleVpcChange = async (vpcId) => {
    setSelectedVpc(vpcId);
    setProvisionForm(f => ({ ...f, subnetId: '', securityGroupId: '' }));
    await loadVpcResources(vpcId);
  };

  const handleConfigSave = async () => {
    try {
      setConfigLoading(true);
      setError(null);

      const res = await fetch(`${API_URL}/aws/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configForm)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to save configuration');
      }

      setSuccess('AWS credentials saved successfully');
      setShowConfigDialog(false);
      await loadAwsStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleCreateSecurityGroup = async () => {
    try {
      setError(null);

      const res = await fetch(`${API_URL}/aws/security-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: sgForm.name,
          description: sgForm.description || 'Kafka cluster security group',
          vpcId: selectedVpc
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to create security group');
      }

      setSuccess(`Security group "${sgForm.name}" created`);
      setShowCreateSGDialog(false);
      setSgForm({ name: '', description: '' });
      await loadVpcResources(selectedVpc);
      setProvisionForm(f => ({ ...f, securityGroupId: data.id }));
    } catch (e) {
      setError(e.message);
    }
  };

  const handleProvision = async () => {
    try {
      setProvisionLoading(true);
      setError(null);

      const res = await fetch(`${API_URL}/aws/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(provisionForm)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to provision instances');
      }

      setSuccess(`Successfully provisioned ${data.instances.length} instances`);
      setShowProvisionDialog(false);
      await loadResources();
    } catch (e) {
      setError(e.message);
    } finally {
      setProvisionLoading(false);
    }
  };

  const handleInstanceAction = async (action, instanceIds) => {
    try {
      setError(null);
      
      const res = await fetch(`${API_URL}/aws/instances/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instanceIds })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || `Failed to ${action} instances`);
      }

      setSuccess(`Instances ${action}ed successfully`);
      await loadResources();
    } catch (e) {
      setError(e.message);
    }
  };

  const getStateColor = (state) => {
    switch (state) {
      case 'running': return 'text-green-500';
      case 'stopped': return 'text-red-500';
      case 'pending': case 'stopping': return 'text-amber-500';
      case 'terminated': return 'text-gray-500';
      default: return 'text-muted-foreground';
    }
  };

  const getStateBadge = (state) => {
    switch (state) {
      case 'running': return <Badge className="bg-green-500/20 text-green-500 border-green-500/30">{state}</Badge>;
      case 'stopped': return <Badge className="bg-red-500/20 text-red-500 border-red-500/30">{state}</Badge>;
      case 'pending': case 'stopping': return <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30">{state}</Badge>;
      default: return <Badge variant="outline">{state}</Badge>;
    }
  };

  // Group instances by cluster
  const instancesByCluster = instances.reduce((acc, inst) => {
    const cluster = inst.cluster || 'Unknown';
    if (!acc[cluster]) acc[cluster] = [];
    acc[cluster].push(inst);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading AWS status...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Cloud className="h-6 w-6 text-orange-500" />
          <div>
            <h2 className="text-xl font-semibold">AWS Infrastructure</h2>
            <p className="text-sm text-muted-foreground">Provision and manage EC2 instances for Kafka clusters</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadAwsStatus}>
            <RefreshCw className="h-4 w-4 mr-1" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowConfigDialog(true)}>
            <Settings className="h-4 w-4 mr-1" />Configure
          </Button>
          {awsStatus?.configured && (
            <Button onClick={() => setShowProvisionDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />Provision Instances
            </Button>
          )}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>×</Button>
        </Alert>
      )}
      {success && (
        <Alert className="border-green-500/50 bg-green-500/10">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-600">{success}</AlertDescription>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setSuccess(null)}>×</Button>
        </Alert>
      )}

      {/* AWS Status Card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={cn(
                "p-2 rounded-full",
                awsStatus?.configured ? "bg-green-500/20" : "bg-amber-500/20"
              )}>
                <Cloud className={cn(
                  "h-5 w-5",
                  awsStatus?.configured ? "text-green-500" : "text-amber-500"
                )} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">AWS Connection</span>
                  {awsStatus?.configured ? (
                    <Badge className="bg-green-500/20 text-green-500 border-green-500/30">Connected</Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30">Not Configured</Badge>
                  )}
                </div>
                {awsStatus?.config && (
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    <span><Globe className="h-3 w-3 inline mr-1" />{awsStatus.config.region}</span>
                    <span><Key className="h-3 w-3 inline mr-1" />{awsStatus.config.accessKeyId}</span>
                  </div>
                )}
              </div>
            </div>
            {!awsStatus?.configured && (
              <Button onClick={() => setShowConfigDialog(true)}>
                <Key className="h-4 w-4 mr-1" />Add Credentials
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Instances */}
      {awsStatus?.configured && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="h-4 w-4" />
              Managed Instances
              <Badge variant="outline">{instances.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {instances.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Server className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No instances provisioned yet</p>
                <Button variant="outline" className="mt-3" onClick={() => setShowProvisionDialog(true)}>
                  <Plus className="h-4 w-4 mr-1" />Provision Instances
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(instancesByCluster).map(([cluster, clusterInstances]) => (
                  <div key={cluster} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4 text-primary" />
                        <span className="font-medium">{cluster}</span>
                        <Badge variant="outline">{clusterInstances.length} instances</Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        {clusterInstances.some(i => i.state === 'stopped') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleInstanceAction('start', clusterInstances.filter(i => i.state === 'stopped').map(i => i.id))}
                          >
                            <Play className="h-3 w-3 mr-1" />Start All
                          </Button>
                        )}
                        {clusterInstances.some(i => i.state === 'running') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleInstanceAction('stop', clusterInstances.filter(i => i.state === 'running').map(i => i.id))}
                          >
                            <Square className="h-3 w-3 mr-1" />Stop All
                          </Button>
                        )}
                        {onUseInstances && clusterInstances.filter(i => i.state === 'running').length >= 1 && (
                          <Button
                            size="sm"
                            onClick={() => onUseInstances(clusterInstances.filter(i => i.state === 'running'))}
                          >
                            Use for Kafka
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {clusterInstances.map(inst => (
                        <div
                          key={inst.id}
                          className="flex items-center justify-between p-2 rounded border bg-muted/30"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono truncate">{inst.id}</span>
                              {getStateBadge(inst.state)}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                              {inst.publicIp && <span>Public: {inst.publicIp}</span>}
                              <span>Private: {inst.privateIp}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                              <span><Cpu className="h-2.5 w-2.5 inline" /> {inst.type}</span>
                              <span>{inst.az}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {inst.state === 'stopped' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handleInstanceAction('start', [inst.id])}
                                title="Start"
                              >
                                <Play className="h-3 w-3 text-green-500" />
                              </Button>
                            )}
                            {inst.state === 'running' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handleInstanceAction('stop', [inst.id])}
                                title="Stop"
                              >
                                <Square className="h-3 w-3 text-amber-500" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                if (window.confirm(`Terminate instance ${inst.id}? This cannot be undone.`)) {
                                  handleInstanceAction('terminate', [inst.id]);
                                }
                              }}
                              title="Terminate"
                            >
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configure Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              AWS Credentials
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Credentials are stored locally and used only for EC2 management. Use an IAM user with limited EC2 permissions.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <Label>Region</Label>
              <Select value={configForm.region} onValueChange={v => setConfigForm(f => ({ ...f, region: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {regions.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Access Key ID</Label>
              <Input
                value={configForm.accessKeyId}
                onChange={e => setConfigForm(f => ({ ...f, accessKeyId: e.target.value }))}
                placeholder="AKIAIOSFODNN7EXAMPLE"
              />
            </div>

            <div className="space-y-2">
              <Label>Secret Access Key</Label>
              <div className="relative">
                <Input
                  type={showSecretKey ? 'text' : 'password'}
                  value={configForm.secretAccessKey}
                  onChange={e => setConfigForm(f => ({ ...f, secretAccessKey: e.target.value }))}
                  placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                >
                  {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={handleConfigSave} disabled={configLoading || !configForm.accessKeyId || !configForm.secretAccessKey}>
              {configLoading ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
              Save & Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provision Dialog */}
      <Dialog open={showProvisionDialog} onOpenChange={setShowProvisionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              Provision EC2 Instances
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cluster Name</Label>
                <Input
                  value={provisionForm.clusterName}
                  onChange={e => setProvisionForm(f => ({ ...f, clusterName: e.target.value }))}
                  placeholder="kafka-cluster"
                />
              </div>
              <div className="space-y-2">
                <Label>Number of Instances</Label>
                <Select value={String(provisionForm.count)} onValueChange={v => setProvisionForm(f => ({ ...f, count: parseInt(v) }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} instance{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Instance Type</Label>
              <Select value={provisionForm.instanceType} onValueChange={v => setProvisionForm(f => ({ ...f, instanceType: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {instanceTypes.map(t => (
                    <SelectItem key={t.type} value={t.type}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{t.type}</span>
                        <span className="text-muted-foreground text-xs">- {t.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Operating System</Label>
              <Select value={provisionForm.osType} onValueChange={v => setProvisionForm(f => ({ ...f, osType: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {osTypes.map(os => (
                    <SelectItem key={os.value} value={os.value}>{os.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>VPC</Label>
              <Select value={selectedVpc} onValueChange={handleVpcChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select VPC" />
                </SelectTrigger>
                <SelectContent>
                  {vpcs.map(vpc => (
                    <SelectItem key={vpc.id} value={vpc.id}>
                      {vpc.name} ({vpc.id}) - {vpc.cidr} {vpc.isDefault && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Subnet</Label>
              <Select 
                value={provisionForm.subnetId} 
                onValueChange={v => setProvisionForm(f => ({ ...f, subnetId: v }))}
                disabled={!selectedVpc}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Subnet" />
                </SelectTrigger>
                <SelectContent>
                  {subnets.map(subnet => (
                    <SelectItem key={subnet.id} value={subnet.id}>
                      {subnet.name} ({subnet.id}) - {subnet.az} - {subnet.availableIps} IPs
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Security Group</Label>
                <Button variant="ghost" size="sm" onClick={() => setShowCreateSGDialog(true)}>
                  <Plus className="h-3 w-3 mr-1" />Create New
                </Button>
              </div>
              <Select 
                value={provisionForm.securityGroupId} 
                onValueChange={v => setProvisionForm(f => ({ ...f, securityGroupId: v }))}
                disabled={!selectedVpc}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Security Group" />
                </SelectTrigger>
                <SelectContent>
                  {securityGroups.map(sg => (
                    <SelectItem key={sg.id} value={sg.id}>
                      {sg.name} ({sg.id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Key Pair</Label>
              <Select value={provisionForm.keyName} onValueChange={v => setProvisionForm(f => ({ ...f, keyName: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Key Pair" />
                </SelectTrigger>
                <SelectContent>
                  {keyPairs.map(kp => (
                    <SelectItem key={kp.name} value={kp.name}>{kp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                You'll need the corresponding private key file to SSH into instances
              </p>
            </div>

            <div className="space-y-2">
              <Label>Volume Size (GB)</Label>
              <Input
                type="number"
                min={20}
                max={1000}
                value={provisionForm.volumeSize}
                onChange={e => setProvisionForm(f => ({ ...f, volumeSize: parseInt(e.target.value) || 50 }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProvisionDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleProvision} 
              disabled={provisionLoading || !provisionForm.keyName || !provisionForm.securityGroupId || !provisionForm.subnetId}
            >
              {provisionLoading ? (
                <><RefreshCw className="h-4 w-4 mr-1 animate-spin" />Provisioning...</>
              ) : (
                <><Plus className="h-4 w-4 mr-1" />Provision {provisionForm.count} Instance{provisionForm.count > 1 ? 's' : ''}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Security Group Dialog */}
      <Dialog open={showCreateSGDialog} onOpenChange={setShowCreateSGDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Create Security Group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription className="text-xs">
                This will create a security group with ports open for Kafka (9092, 9093), ZooKeeper (2181, 2888, 3888), ksqlDB (8088), and SSH (22).
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={sgForm.name}
                onChange={e => setSgForm(f => ({ ...f, name: e.target.value }))}
                placeholder="kafka-cluster-sg"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={sgForm.description}
                onChange={e => setSgForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Security group for Kafka cluster"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSGDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateSecurityGroup} disabled={!sgForm.name}>
              <Plus className="h-4 w-4 mr-1" />Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AWSInfrastructure;
